import { useState } from "react";
import * as Yup from "yup";
import { IUserSignUpInitialValue, IUserSignUpOutput } from "@types";
import auth from "@api/auth";
import { getCookie } from "@utils/Cookies";
import { COOKIE_LOGIN_TOKEN_KEY } from "@utils/constants";
import { useAppContext } from "@context/app";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import parsePhoneNumber from "libphonenumber-js";
import { segmentEvent, segmentGetAnonymousId, setSegmentIdentifyPhoneNumber } from "@utils/segment";
import { setExternalUserId } from "@utils/oneSignal";
import { useUI } from "@context/ui-managed-context";
import { userApi } from "gate";

export const useRegisterForm = (onSwitchView: (view: string) => void, onClose: () => void) => {
  const { setUserLoggedIn, country, area, syncData } = useAppContext();
  const { setModalView, openModal } = useUI();
  const { locale, pathname, reload } = useRouter();
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fromPage =
    pathname === "/cart" ? "cart" : pathname === "/product/[slug]" ? "product_details" : "menu";

  const signUpValues: IUserSignUpInitialValue = {
    email: "",
    password: "",
    name: "",
    countryCode: country?.dialcode || "",
    phoneNumber: "",
  };

  const SignUpSchema = Yup.object()
    .shape({
      name: Yup.string()
        .min(3, t("client_errors_minchar").replace("@", "3"))
        .max(50, t("client_errors_maxnumberchar").replace("@", "50"))
        .required(t("client_errors_fieldisrequiered")),
      password: Yup.string()
        .required(t("client_errors_fieldisrequiered"))
        .min(8, t("client_errors_minchar").replace("@", "8")),
      email: Yup.string()
        .email(t("client_errors_emailnotvalid"))
        .required(t("client_errors_fieldisrequiered")),
      countryCode: Yup.string().notRequired(),
      phoneNumber: Yup.string().notRequired(),
    })
    .test("phoneValidation", t("client__errors_invalidphonenumber"), function (values) {
      const { countryCode, phoneNumber } = values;
      if (syncData?.phone_field_on_registration_page_mode === "mandatory") {
        if (!countryCode) {
          return this.createError({
            path: "countryCode",
            message: t("client_errors_fieldisrequiered"),
          });
        }
        if (!phoneNumber) {
          return this.createError({
            path: "phoneNumber",
            message: t("client_errors_fieldisrequiered"),
          });
        }
      }

      if (countryCode && phoneNumber) {
        try {
          const phoneNumberObj = parsePhoneNumber(`${countryCode}${phoneNumber}`);
          if (!phoneNumberObj?.isValid()) {
            return this.createError({
              path: "phoneNumber",
              message: t("client__errors_invalidphonenumber"),
            });
          }
        } catch (error) {
          return this.createError({
            path: "phoneNumber",
            message: t("client__errors_invalidphonenumber"),
          });
        }
      }

      return true;
    });

  const handleRegister = async (values: IUserSignUpInitialValue, actions: any) => {
    setIsSubmitting(true);
    try {
      const data: IUserSignUpOutput = {
        name: values.name,
        email: values.email,
        password: values.password,
        phone: values.phoneNumber ? `${values.countryCode}${values.phoneNumber}` : undefined,
      };
      const res = await auth.signUp(data).fetch();
      getCookie().set(COOKIE_LOGIN_TOKEN_KEY, res.data.access_token, {}, 365);
      setExternalUserId(`${res.data.user.id}`, res.data.user.os_id_hash);
      setSegmentIdentifyPhoneNumber(res.data.user, segmentGetAnonymousId());
      const eventData = {
        user_id: res.data.user.id || -1,
        action: "Register",
        language: locale?.includes("en") ? "English" : "Arabic",
        country_id: country?.id,
        country_slug: country?.slug,
        area_id: area?.id,
        area_slug: area?.slug,
        from_page: fromPage,
        from_entity: "",
        from_entity_slug: "",
        from_page_section: "",
        from_page_section_entity: "",
        from_page_section_slug: "",
        social_login: "",
      };
      segmentEvent("User Registered", eventData);
      segmentEvent("User Logged In", { ...eventData, is_by_phone: false, phone_number: "" });
      setUserLoggedIn({ isLoggedIn: true });
      if (
        (syncData?.phone_field_on_registration_page_mode === "mandatory" || values.phoneNumber) &&
        syncData?.phone_verification.mode !== "none"
      ) {
        userApi
          .sendCode({ phone: `${values.countryCode}${values.phoneNumber}` })
          .fetch()
          .then((res) => {
            setModalView(
              "VERIFY_PHONE_NUMBER",
              false,
              `${values.countryCode}${values.phoneNumber}`,
              "OTP",
              res.data.wait
            );
          })
          .catch(() => {
            setModalView(
              "VERIFY_PHONE_NUMBER",
              false,
              `${values.countryCode}${values.phoneNumber}`,
              "PHONE"
            );
          })
          .finally(() => {
            openModal();
            actions.setSubmitting(false);
          });
      } else {
        reload();
        onClose();
        actions.setSubmitting(false);
      }
    } catch (error) {
      console.error(error);
      actions.setSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    signUpValues,
    SignUpSchema,
    handleRegister,
    isSubmitting,
    countries: syncData?.countries || [],
    locale,
    t,
    isRequired: syncData?.phone_field_on_registration_page_mode === "mandatory",
    isDisablePhoneField: syncData?.phone_field_on_registration_page_mode === "none",
  };
};